import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import { useMutation } from '~/hooks/use-mutation'
import { useQuery } from '~/hooks/use-query'
import { useToast } from '~/hooks/use-toast'
import { useAppStore } from '~/stores/app'
import type { AdminUser, AdminUserDetail } from '~/types/admin-user'
import type { ReferralDetailResponse } from '~/types/referral'
import { DialogId } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'
import { Role } from '~/types/user'

type UserDetailDialogProps = {
  user: AdminUser | null
}

export function UserDetailDialog({ user }: UserDetailDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showSuccess } = useToast()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const openDialog = useAppStore((state) => state.openDialog)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isOpen = openDialogIds.includes(DialogId.DETAIL_USER)

  const { data, isLoading } = useQuery<{ user: AdminUserDetail }>({
    queryKey: QueryKey.ADMIN_USER_DETAIL,
    idParams: user ? { id: user.id } : undefined,
    options: {
      enabled: !!user && isOpen,
    },
  })

  // Introductions this user made (same count as the Referrals list). Reuses the
  // referral detail endpoint; a soft-deleted-referrer id still resolves, so
  // this never 404s for an opened user.
  const { data: referralData } = useQuery<ReferralDetailResponse>({
    queryKey: QueryKey.ADMIN_REFERRAL_DETAIL,
    idParams: user ? { id: user.id } : undefined,
    options: {
      enabled: !!user && isOpen,
    },
  })
  const introductionCount = referralData?.referredUsers.length ?? 0

  const { mutate: setEmailVerified, isPending: isUpdatingEmailVerified } = useMutation<
    { user: AdminUser },
    { emailVerified: boolean }
  >({
    mutationKey: MutationKey.UPDATE_USER_EMAIL_VERIFIED,
    method: 'PATCH',
    idParams: user ? { id: user.id } : undefined,
    options: {
      onSuccess: () => {
        showSuccess(t('users.emailVerifiedUpdated'))
        void queryClient.invalidateQueries({ queryKey: [QueryKey.ADMIN_USERS] })
        if (user) {
          void queryClient.invalidateQueries({
            queryKey: [QueryKey.ADMIN_USER_DETAIL, { id: user.id }],
          })
        }
      },
    },
  })

  if (!user) return null

  const detail = data?.user
  const stats = detail?.stats
  const currentEmailVerified = detail?.emailVerified ?? user.emailVerified

  const handleChangeRole = () => {
    openDialog(DialogId.UPDATE_USER_ROLE)
  }

  const handleGrantPoints = () => {
    openDialog(DialogId.GRANT_USER_POINTS)
  }

  const handleToggleEmailVerified = () => {
    setEmailVerified({ emailVerified: !currentEmailVerified })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(DialogId.DETAIL_USER)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('users.details')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <DetailItem label={t('users.username')} value={user.username} />
            <DetailItem label={t('users.email')} value={user.email} />
            <DetailItem label={t('users.phone')} value={user.phone ?? '-'} />
            <DetailItem
              label={t('users.role')}
              value={
                <Badge variant={user.role === Role.ADMIN ? 'default' : 'secondary'}>
                  {user.role === Role.ADMIN ? t('users.roleAdmin') : t('users.roleUser')}
                </Badge>
              }
            />
            <DetailItem label={t('users.registrationStep')} value={String(user.registrationStep)} />
            <DetailItem
              label={t('users.phoneVerified')}
              value={user.phoneVerified ? t('common.yes') : t('common.no')}
            />
            <DetailItem
              label={t('users.emailVerified')}
              value={
                <div className="flex items-center gap-2">
                  <Badge variant={currentEmailVerified ? 'success' : 'secondary'}>
                    {currentEmailVerified ? t('common.yes') : t('common.no')}
                  </Badge>
                  <Button
                    id="user-detail-toggle-email-verified-button"
                    variant="outline"
                    size="sm"
                    disabled={isUpdatingEmailVerified}
                    onClick={handleToggleEmailVerified}
                  >
                    {currentEmailVerified ? t('users.markEmailUnverified') : t('users.markEmailVerified')}
                  </Button>
                </div>
              }
            />
            <DetailItem label={t('users.name')} value={user.profile?.name ?? t('users.noProfile')} />
            <DetailItem label={t('users.membership')} value={user.profile?.membershipType ?? '-'} />
            <DetailItem
              label={t('users.introductions')}
              value={
                introductionCount > 0 ? (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => {
                      closeDialog(DialogId.DETAIL_USER)
                      navigate(`/referrals/${user.id}`)
                    }}
                  >
                    {introductionCount}
                  </button>
                ) : (
                  '0'
                )
              }
            />
          </div>
          {stats ? (
            <div className="space-y-2 rounded-md border bg-muted/30 p-4">
              <p className="text-sm font-semibold">{t('users.stats')}</p>
              <div className="grid grid-cols-3 gap-4">
                <DetailItem label={t('users.pointBalance')} value={String(stats.pointBalance)} />
                <DetailItem label={t('users.deviceTokens')} value={String(stats.deviceTokenCount)} />
                <DetailItem label={t('users.categories')} value={String(stats.categoryCount)} />
              </div>
            </div>
          ) : (
            isLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
          {detail?.pointLogs ? (
            <div className="space-y-2 rounded-md border bg-muted/30 p-4">
              <p className="text-sm font-semibold">{t('users.pointLogs')}</p>
              {detail.pointLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('users.noPointLogs')}</p>
              ) : (
                <div className="max-h-64 space-y-2 overflow-auto">
                  {detail.pointLogs.map((log) => (
                    <div key={log.id} className="rounded border bg-background p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p
                          className={`text-sm font-semibold ${
                            log.type === 'credit' ? 'text-emerald-600' : 'text-destructive'
                          }`}
                        >
                          {log.type === 'credit' ? '+' : ''}
                          {log.amount.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">{new Date(log.createdAt).toLocaleString()}</p>
                      </div>
                      <p className="mt-1 text-sm">{log.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
        <DialogFooter className="justify-between sm:justify-between">
          <Button id="user-detail-grant-points-button" variant="outline" onClick={handleGrantPoints}>
            {t('users.grantPointsAction')}
          </Button>
          <Button id="user-detail-change-role-button" onClick={handleChangeRole}>
            {t('users.changeRole')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="text-sm">{value}</div>
    </div>
  )
}
